import React, { useState, useEffect, useCallback } from 'react';
import { useThemeMode } from '../theme/ThemeContext';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { getSummary, updateSummary } from '../api/taskApi';
import { format } from 'date-fns';
import tomato from '../assets/images/Pomodoro Timer.png';
import timerGif from '../assets/images/timer.gif';
import HelpButton from './onboarding/HelpButton';

const ActivityFooter = () => {
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';
    const selectedOrganisation = useSelector((state) => state.organisation.selectedOrganisation);
    const orgId = selectedOrganisation?.id || selectedOrganisation?.organisation_id || selectedOrganisation?.ID;

    const [dayStarted, setDayStarted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [stoppingActivity, setStoppingActivity] = useState(false);
    const [rotations, setRotations] = useState({ tomato: 0, timer: 0, trophy: 0 });

    const checkActivityStatus = useCallback(async () => {
        if (!orgId) return;
        try {
            const summary = await getSummary(orgId);
            const sd = summary?.summaryData || summary;
            setSummaryData(sd);
            const hasStart = !!sd?.start_activity;
            const hasEnd = !!sd?.end_activity;
            const isBreak = sd?.break === 1 || sd?.break === true;
            if (hasStart && !hasEnd) {
                setDayStarted(true);
                setIsPlaying(!isBreak);
            } else {
                setDayStarted(false);
                setIsPlaying(false);
            }
        } catch (err) { /* ignore */ }
    }, [orgId]);

    useEffect(() => {
        checkActivityStatus();
    }, [checkActivityStatus]);

    // Listen for activityStarted event to refresh footer state
    useEffect(() => {
        const handleActivityStarted = () => checkActivityStatus();
        window.addEventListener('activityStarted', handleActivityStarted);
        return () => window.removeEventListener('activityStarted', handleActivityStarted);
    }, [checkActivityStatus]);

    const handlePauseResume = async () => {
        if (!orgId) return;
        try {
            if (isPlaying) {
                await updateSummary({ organisationID: orgId, break: 1, emoji_feedback: 1 });
                setIsPlaying(false);
            } else {
                await updateSummary({ organisationID: orgId, break_duration: 30, break: 0, emoji_feedback: 1 });
                setIsPlaying(true);
            }
            const summary = await getSummary(orgId);
            setSummaryData(summary?.summaryData || summary);
        } catch (err) { /* ignore */ }
    };

    const handleStopDay = async () => {
        if (stoppingActivity || !orgId) return;
        try {
            setStoppingActivity(true);
            // Update UI immediately (optimistic)
            setDayStarted(false);
            setIsPlaying(false);
            const now = new Date();
            const endActivityDate = format(now, 'yyyy-MM-dd HH:mm:ss');
            await updateSummary({
                emoji: 4,
                end_activity: endActivityDate,
                organisationID: orgId,
                sendReport: true,
                auto__end: 2
            });
            const summary = await getSummary(orgId);
            setSummaryData(summary?.summaryData || summary);
        } catch (err) {
            // Revert on error
            checkActivityStatus();
        }
        finally { setStoppingActivity(false); }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: isDark ? '#0e6c52' : '#27ae86',
                borderRadius: 0,
                padding: (orgId && dayStarted) ? '6px 20px' : '12px 24px',
                margin: 0,
                boxShadow: isDark ? '0 -2px 8px rgba(0, 0, 0, 0.4)' : '0 -2px 8px rgba(39, 174, 134, 0.3)',
                zIndex: 1100
            }}
        >
            {/* Centru: activity controls */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                {/* Play/Pause Button - doar când avem organizație */}
                {orgId && (
                <Tooltip title={dayStarted && !isPlaying ? "Resume Activity" : (isPlaying ? "Pause Activity" : "Start Activity")}>
                    <IconButton
                        onClick={() => {
                            if (!dayStarted) {
                                // Open start activity dialog via custom event
                                window.dispatchEvent(new CustomEvent('openStartActivityDialog'));
                            } else {
                                handlePauseResume();
                            }
                        }}
                        sx={{
                            width: 46,
                            height: 46,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {dayStarted && !isPlaying ? <PlayCircleIcon /> : (isPlaying ? <PauseIcon /> : <PlayArrowIcon />)}
                    </IconButton>
                </Tooltip>
                )}

                {orgId && dayStarted && (
                    <>
                        {/* Pomodoro */}
                        <Tooltip title="Pomodoro Timer">
                            <Box
                                onClick={() => {
                                    setRotations(prev => ({ ...prev, tomato: (prev.tomato || 0) + 360 }));
                                    window.dispatchEvent(new CustomEvent('openStartActivityDialog'));
                                }}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            >
                                <Box
                                    sx={{
                                        width: 50, height: 50,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.6s ease',
                                        transform: `rotate(${rotations.tomato || 0}deg)`,
                                        '&:hover': { transform: `rotate(${rotations.tomato || 0}deg) scale(1.1)` }
                                    }}
                                >
                                    <img src={tomato} alt="Pomodoro" style={{ width: '46px', height: '46px', objectFit: 'contain' }} />
                                </Box>
                                <Typography sx={{ fontSize: '10px', color: '#ffffff', fontWeight: 600, lineHeight: 1 }}>
                                    {(() => {
                                        const ft = summaryData?.focus_time || summaryData?.focusTime || summaryData?.focus_time_seconds || 0;
                                        if (typeof ft === 'number' && ft > 0) {
                                            const h = Math.floor(ft / 3600);
                                            const m = Math.floor((ft % 3600) / 60);
                                            return h > 0 ? `${h}h ${m}m` : `${m}m`;
                                        }
                                        return '0s';
                                    })()}
                                </Typography>
                            </Box>
                        </Tooltip>

                        {/* Timer - Opens activity dialog */}
                        <Tooltip title="Timer">
                            <Box
                                onClick={() => {
                                    setRotations(prev => ({ ...prev, timer: (prev.timer || 0) + 360 }));
                                    window.dispatchEvent(new CustomEvent('openStartActivityDialog'));
                                }}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            >
                                <Box
                                    sx={{
                                        width: 50, height: 50,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.6s ease',
                                        transform: `rotate(${rotations.timer || 0}deg)`,
                                        '&:hover': { transform: `rotate(${rotations.timer || 0}deg) scale(1.1)` }
                                    }}
                                >
                                    <img src={timerGif} alt="Timer" style={{ width: '46px', height: '46px', objectFit: 'contain' }} />
                                </Box>
                                {summaryData?.start_activity && (
                                    <Typography sx={{ fontSize: '10px', color: '#ffffff', fontWeight: 600, lineHeight: 1 }}>
                                        {(() => {
                                            try {
                                                const startDate = new Date(summaryData.start_activity);
                                                const now = new Date();
                                                const diffMs = now - startDate;
                                                const h = Math.floor(diffMs / (1000 * 60 * 60));
                                                const m = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                                return h > 0 ? `${h}h ${m}m` : `${m}m`;
                                            } catch (e) { return '0m'; }
                                        })()}
                                    </Typography>
                                )}
                            </Box>
                        </Tooltip>

                        {/* Trophy */}
                        <Tooltip title="Trophy">
                            <Box
                                onClick={() => {
                                    setRotations(prev => ({ ...prev, trophy: (prev.trophy || 0) + 360 }));
                                    window.dispatchEvent(new CustomEvent('openStartActivityDialog'));
                                }}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            >
                                <IconButton
                                    sx={{
                                        width: 46, height: 46,
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        color: '#FFD700',
                                        transition: 'transform 0.6s ease',
                                        transform: `rotate(${rotations.trophy || 0}deg)`,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            transform: `rotate(${rotations.trophy || 0}deg) scale(1.1)`
                                        }
                                    }}
                                >
                                    <EmojiEventsIcon sx={{ fontSize: '28px' }} />
                                </IconButton>
                                <Typography sx={{ fontSize: '10px', color: '#ffffff', fontWeight: 600, lineHeight: 1 }}>
                                    {summaryData?.resolvedTasks?.resolvedTasksCount ?? summaryData?.done_count ?? 0}
                                </Typography>
                            </Box>
                        </Tooltip>

                        {/* Stop */}
                        <Tooltip title="Stop Activity">
                            <IconButton
                                onClick={handleStopDay}
                                disabled={stoppingActivity}
                                sx={{
                                    width: 46, height: 46,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: '#ffffff',
                                    '&:hover': {
                                        backgroundColor: stoppingActivity ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)',
                                        transform: stoppingActivity ? 'none' : 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {stoppingActivity ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : <StopIcon />}
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>

            {/* Dreapta: Support + Help */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Tooltip title="Support">
                    <Box
                        onClick={() => window.dispatchEvent(new CustomEvent('openSupportDialog'))}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            '&:hover': {
                                backgroundColor: isDark ? '#3a3a3a' : '#f0f0f0',
                                transform: 'scale(1.05)'
                            }
                        }}
                    >
                        <QuestionAnswerIcon sx={{ color: '#ff6b6b', fontSize: '18px' }} />
                        <Typography sx={{ color: '#ff6b6b', fontWeight: 600, fontSize: '13px' }}>
                            Support
                        </Typography>
                    </Box>
                </Tooltip>
                <HelpButton variant="footer" onOpenGuide={() => window.dispatchEvent(new CustomEvent('openWelcomeGuide'))} />
            </Box>
        </Box>
    );
};

export default ActivityFooter;
